import type { DataOf } from '@elara/firebolt/shared';

import type { ToolCallType } from '#models/ToolCallSpec.js';
import type { DataPartToTextProcessor } from '#processing/index.js';
import type { AgentMetadata } from './schemas/AgentMetadataSchema.js';
import type {
  ActionMetadata,
  ClientMessagePart,
  MessagePart,
  ToolAction,
  ToolRequestPart,
  ToolResponsePart,
} from './schemas/message-schemas.js';

import { z } from 'zod';

import { ResourceMetaSchema } from '@elara/core/agent/resources';
import { objectKey, stringify } from '@elara/core/introspection';
import { collectionPath, defineModelSpec, orderBy } from '@elara/firebolt/shared';
import { zFirestampOptional } from '@elara/firebolt/zod-types';

import {
  isDataPart,
  isMediaPart,
  isToolRequestPart,
  isToolResponsePart,
  MessagePartSchema,
  MessageRoleSchema,
} from './schemas/message-schemas.js';
import { chatId, id } from './schemas/zod-types.js';

/**
 * The canonical `ModelSpec` for the messages sub-collection within a chat.
 * It defines the path (`chats/{chatId}/messages/{id}`) and the data shape for a message document.
 */
export const ChatMessageModelSpec = defineModelSpec({
  path: collectionPath`chats/${chatId}/messages/${id}`,
  shape: {
    role: MessageRoleSchema,
    content: z.array(MessagePartSchema),
    metadata: z
      .looseObject({ resources: z.array(ResourceMetaSchema).optional(), thread: z.string().optional() })
      .optional(),
    userId: z.string().optional(),
    chatId: z.string(),
    replyTo: z.string().optional(),
    editedAt: zFirestampOptional(),
    deleted: z.boolean().default(false).optional(),
    deletedAt: zFirestampOptional(),
    // Add sequence number for deterministic ordering when timestamps are identical
    sequence: z
      .number()
      .int()
      .min(0)
      .optional()
      .describe('Sequence number for ordering messages created at the same time'),
  },
  // Sort by createdAt first, then by document ID for deterministic ordering
  // Note: sequence field is available for new messages but we can't order by it in Firestore
  // as existing messages don't have this field (would require an index)
  constraints: [orderBy('createdAt'), orderBy('__name__')],
});

export type ChatMessageModelSpec = typeof ChatMessageModelSpec;

/**
 * Parses a message's content array and extracts all `ToolRequestPart` and `ToolResponsePart` items,
 * transforming them into a client-side `ToolCall` format.
 *
 * @returns An array of `ToolCall` objects suitable for rendering in the UI.
 * @param msg
 */
export function extractToolCalls(
  msg: ChatMessageData,
  toolCalls = new Map<string, ClientToolCall>(),
): Map<string, ClientToolCall> {
  const accumulator = new ToolCallAccumulator(toolCalls);

  for (const [idx, part] of msg.content.entries()) {
    if (isToolRequestPart(part)) {
      accumulator.handleToolRequest(idx, part);
    } else if (isToolResponsePart(part)) {
      accumulator.handleToolResponse(part);
    }
  }

  return accumulator.flush();
}

function normalizeToolRef(ref: unknown): string | undefined {
  if (ref === undefined || ref === null) return;
  const refString = String(ref).trim();
  // sometimes model returns '0' for a bunch of tool calls, so it's not a valid ref
  if (!refString || refString === '0') return;

  return refString;
}

function deriveToolCallId(idx: number, name: string | undefined, ref?: string): string {
  return ref ?? `idx:${idx}:${name ?? 'tool'}`;
}

class ToolCallAccumulator {
  constructor(existingCalls: Map<string, ClientToolCall>) {
    this.existingCalls = existingCalls;
  }

  handleToolRequest(idx: number, part: ToolRequestPart): void {
    const normalizedRef = normalizeToolRef(part.toolRequest.ref);
    const toolCallId = deriveToolCallId(idx, part.toolRequest.name, normalizedRef);
    const pendingCall: PendingToolCall = {
      ...part.toolRequest,
      id: toolCallId,
      name: part.toolRequest.name,
      input: part.toolRequest.input as Record<string, unknown>,
      status: 'pending',
    };
    console.debug(`Adding pending tool call: ${JSON.stringify(pendingCall)}`, { pendingCall, part });
    this.addPending(pendingCall);
  }

  handleToolResponse(part: ToolResponsePart): void {
    const normalizedRef = normalizeToolRef(part.toolResponse.ref);
    const toolRequest =
      this.resolvePending(normalizedRef) ??
      (normalizedRef ? this.existingCalls.get(normalizedRef) : undefined) ??
      this.popFallbackPending();
    if (!toolRequest) return;

    const output = part.toolResponse.output;
    if (typeof output === 'object' && output !== null && 'error' in output) {
      this.completed.push({ ...toolRequest, status: 'failed', error: stringify(output.error) });
    } else {
      this.completed.push({ ...toolRequest, status: 'completed', output });
    }
  }

  flush(): Map<string, ClientToolCall> {
    for (const finished of this.completed) {
      this.existingCalls.set(finished.id, finished);
    }

    for (const pending of this.pendingStack) {
      this.existingCalls.set(pending.id, pending);
    }

    return this.existingCalls;
  }

  private readonly existingCalls: Map<string, ClientToolCall>;
  private readonly pendingStack: PendingToolCall[] = [];
  private readonly pendingById = new Map<string, PendingToolCall>();
  private readonly completed: FinishedToolCall[] = [];

  private addPending(call: PendingToolCall): void {
    this.pendingStack.push(call);
    this.pendingById.set(call.id, call);
  }

  private resolvePending(normalizedRef: string | undefined): PendingToolCall | undefined {
    if (!normalizedRef) return;
    const pending = this.pendingById.get(normalizedRef);
    if (!pending) return;

    this.pendingById.delete(normalizedRef);
    const index = this.pendingStack.findIndex((item) => item.id === normalizedRef);
    if (index !== -1) this.pendingStack.splice(index, 1);

    return pending;
  }

  private popFallbackPending(): PendingToolCall | undefined {
    const fallback = this.pendingStack.pop();
    if (!fallback) return;
    this.pendingById.delete(fallback.id);

    return fallback;
  }
}

export type ToolPart = ToolRequestPart | ToolResponsePart;

/**
 * Type guard to check if a string is a valid AttachmentType.
 */
function isValidAttachmentType(type: string): type is AttachmentType {
  const validTypes: AttachmentType[] = ['image', 'document', 'video', 'audio', 'file', 'destination', 'hotel', 'place'];
  return validTypes.includes(type as AttachmentType);
}

/**
 * Validates and creates an Attachment from DataPart data with proper type checking.
 * Returns null if the data structure is invalid or the type is not a valid AttachmentType.
 *
 * @param data - The data object from a DataPart
 * @returns A validated Attachment object or null if validation fails
 */
function createDataAttachment(data: Record<string, unknown>): Attachment | null {
  // Validate required fields
  if (typeof data.type !== 'string' || typeof data.id !== 'string') {
    return null;
  }

  // Validate that type is a valid AttachmentType using type guard
  if (!isValidAttachmentType(data.type)) {
    return null;
  }

  const attachmentType = data.type; // TypeScript now knows this is AttachmentType
  const typeLabel = attachmentType.charAt(0).toUpperCase() + attachmentType.slice(1);

  const attachment: Attachment = {
    id: `${attachmentType}-${data.id}`,
    type: attachmentType,
    name: (typeof data.displayName === 'string' ? data.displayName : typeLabel) || typeLabel,
    size: 0,
    ...(attachmentType === 'destination' || attachmentType === 'hotel' || attachmentType === 'place'
      ? { referenceId: data.id }
      : {}),
  };

  return attachment;
}

/**
 * Parses a message's content array and extracts all `MediaPart` and `DataPart` items,
 * transforming them into a client-side `Attachment` format.
 *
 * @param content - The content array from a `ChatMessageData` object.
 * @returns An array of `Attachment` objects suitable for rendering in the UI.
 */
export function extractAttachments(content: MessagePart[]): Attachment[] {
  // Ensure content is a valid array before processing.
  if (!content || !Array.isArray(content)) {
    console.warn('[extractAttachments] Invalid content:', content);

    return [];
  }

  return content.flatMap((part) => {
    if (!part) return [];

    // Handle DataPart
    if (isDataPart(part) && part.data && typeof part.data === 'object') {
      const data = part.data as Record<string, unknown>;
      const attachment = createDataAttachment(data);
      return attachment ? [attachment] : [];
    }

    // Handle MediaPart (existing logic)
    const isMedia = isMediaPart(part);
    if (!isMedia) return [];
    const type = attachmentType(part.media.contentType);
    // Extract filename from URL, handling Firebase Storage URLs
    let filename = 'attachment';
    try {
      const url = new URL(part.media.url);
      const pathname = decodeURIComponent(url.pathname);
      const pathParts = pathname.split('/');
      filename = pathParts.at(-1) || 'attachment';
      // Remove any query parameters from the filename
      if (filename.includes('?')) {
        filename = filename.split('?')[0] || 'attachment';
      }
    } catch {
      // Fallback to simple extraction if URL parsing fails
      filename = part.media.url.split('/').pop()?.split('?')[0] || 'attachment';
    }
    const size = 'size' in part.media ? part.media.size : 0;

    return [
      {
        id: part.media.url, // Using URL as a unique ID for simplicity.
        type,
        name: filename,
        mimeType: part.media.contentType || 'application/octet-stream',
        size,
        url: part.media.url,
      } as Attachment,
    ];
  });
}

/**
 * Determines the specific `AttachmentType` from a MIME type string.
 * @param [contentType] - The MIME type of the file (e.g., 'image/png', 'application/pdf').
 * @returns The categorized attachment type.
 * @private
 */
function attachmentType(contentType?: string): AttachmentType {
  if (!contentType) return 'file';
  if (contentType.includes('pdf')) return 'document';
  if (contentType.startsWith('image/')) return 'image';
  if (contentType.startsWith('video/')) return 'video';
  if (contentType.startsWith('audio/')) return 'audio';

  return 'file';
}

/**
 * Represents the client-side view model for a file attachment.
 * This structure is used by UI components to render attachment previews.
 */
export interface Attachment {
  id: AttachmentId;
  type: AttachmentType;
  url?: string;
  name: string;
  size: number;
  mimeType?: string;
  thumbnailUrl?: string | undefined;
  referenceId?: string;
}

/**
 * A type alias for the unique identifier of an attachment.
 */
export type AttachmentId = string;

/**
 * A discriminated union of possible attachment types for UI rendering.
 */
export type AttachmentType = 'image' | 'document' | 'video' | 'audio' | 'file' | 'destination' | 'hotel' | 'place';
/**
 * Defines the logic for extracting tool call information from message content.
 * This utility is consumed by `MessageView` to render tool call status.
 */

export function isToolAction(tool: unknown): tool is ToolAction {
  return objectKey('__action').isPresentIn<ToolAction['__action']>(tool);
}

export function getToolActionMetadata(tool: ToolAction) {
  return tool.__action;
}

// export type ToolCall = PendingToolCall | ExecutingToolCall | FinishedToolCall;

export type ClientToolCall = PendingToolCall | ExecutingToolCall | SuccessfulToolCall | FailedToolCall;

export type ClientFailedToolCall = Omit<FailedToolCall, 'error'> & { error: string };

export interface BaseToolCall {
  id: string;
  name: string | undefined;
  input?: Record<string, unknown>;
  startTime?: number;
  callType?: ToolCallType;
  agent?: AgentMetadata;

  [key: string]: unknown;
}

export type PendingToolCall = { status: 'pending' } & BaseToolCall;

export type ExecutingToolCall = { status: 'executing' } & BaseToolCall;

export type SuccessfulToolCall = { status: 'completed'; endTime?: number; output?: unknown } & BaseToolCall;

export type FailedToolCall = { status: 'failed'; endTime?: number; error?: unknown } & BaseToolCall;

export type FinishedToolCall = SuccessfulToolCall | FailedToolCall;

export const zToolCategory = z.enum([
  'system',
  'internal',
  'search',
  'external',
  'travel',
  'agent',
  'widget',
  'shortlist',
  'itinerary',
] as const);

export type ToolCategory = z.infer<typeof zToolCategory>;

/**
 * A helper function to heuristically detect a tool's category based on its name.
 * @param actionMeta
 * @returns The detected category.
 * @private
 */
export function detectCategory(actionMeta: ActionMetadata): ToolCategory {
  const toolName = actionMeta.name;
  if (actionMeta.metadata?.category) {
    return actionMeta.metadata?.category as ToolCategory;
  }
  if (!toolName) return 'external';
  if (/(search|scraper|url)/i.test(toolName)) {
    return 'search';
  }
  if (/(hotel|weather|flight)/i.test(toolName)) {
    return 'external';
  }

  return 'system';
}

export function getToolCategoryColor(category: ToolCategory | undefined | null) {
  switch (category) {
    case 'system': {
      return 'bg-blue-500/10 text-blue-500';
    }
    case 'internal': {
      return 'bg-orange-500/10 text-orange-500';
    }
    case 'search': {
      return 'bg-green-500/10 text-green-500';
    }
    case 'travel': {
      return 'bg-yellow-500/10 text-yellow-500';
    }
    case 'external': {
      return 'bg-purple-500/10 text-purple-500';
    }
    case 'widget': {
      return 'bg-red-500/10 text-red-500';
    }
    case 'agent': {
      return 'bg-pink-500/10 text-pink-500';
    }
    case 'shortlist': {
      return 'bg-red-500/10 text-red-500';
    }
    case 'itinerary': {
      return 'bg-yellow-500/10 text-yellow-500';
    }
    default: {
      return 'bg-gray-500/10 text-gray-500';
    }
  }
}

const ToolCategoryIcons = {
  system: 'code',
  travel: 'hotel',
  search: 'search',
  external: 'service_toolbox',
  agent: 'smart_toy',
  internal: 'build',
  widget: 'widgets',
  shortlist: 'favorite',
  itinerary: 'event',
} as const;
export type ToolCategoryIcon = (typeof ToolCategoryIcons)[keyof typeof ToolCategoryIcons];

export function getToolCategoryIcon(category: ToolCategory | undefined | null): ToolCategoryIcon {
  return ToolCategoryIcons[category as ToolCategory] ?? ToolCategoryIcons.system;
}

export const AnsweredQuestionSchema = z.record(z.string(), z.string().or(z.array(z.string())));
export type AnsweredQuestion = z.infer<typeof AnsweredQuestionSchema>;

export const AnsweredQuestionsPartSchema = z.object({
  data: z.object({ type: z.literal('answeredQuestions'), answeredQuestions: AnsweredQuestionSchema }),
});

export function isAnsweredQuestionsPart(part: ClientMessagePart): part is AnsweredQuestionsPart {
  return isDataPart(part) && AnsweredQuestionsPartSchema.safeParse(part).success;
}

export type AnsweredQuestionsPart = z.infer<typeof AnsweredQuestionsPartSchema>;

/**
 * DataParts with type+id are user attachments (hotel, destination, etc.). Preserve them
 * so extractAttachments() can show them above the message instead of converting to text.
 */
export function isAttachmentDataPart(part: Parameters<DataPartToTextProcessor['process']>[0]): boolean {
  if (!isDataPart(part) || !part.data || typeof part.data !== 'object') return false;
  const d = part.data as Record<string, unknown>;
  return typeof d.type === 'string' && typeof d.id === 'string';
}

export interface ChatMessageData extends DataOf<ChatMessageModelSpec> {}
